import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    companyName: "",
    mobileNumber: "",
    requirements: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const apiUrl = "http://localhost:58826/api/contact/contactus";

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        setFormData({
          fullName: "",
          email: "",
          companyName: "",
          mobileNumber: "",
          requirements: "",
        });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.errors
          ? JSON.stringify(errorData.errors)
          : "An unknown error occurred.";
        alert(`Submission failed: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred. Please try again later.");
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&family=JetBrains+Mono:wght@400;500&display=swap');
        
        .font-display {
          font-family: 'Inter', sans-serif;
          font-weight: 900;
        }
        
        .font-mono-body {
          font-family: 'JetBrains Mono', monospace;
          font-weight: 400;
        }
        
        .font-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
        }

        .input-field {
          transition: all 0.3s ease;
        }

        .input-field:focus {
          border-color: #8b0000;
          transform: translateY(-2px);
        }

        .submit-btn {
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(139, 0, 0, 0.3);
        }

        .submit-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s;
        }

        .submit-btn:hover::before {
          left: 100%;
        }
      `}</style>

      <Navbar />

      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100">
        {/* Decorative Wave Patterns */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <svg className="absolute top-0 left-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="wave-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <path d="M0 50 Q 25 25, 50 50 T 100 50" stroke="#8b0000" strokeWidth="0.5" fill="none" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#wave-pattern)" />
          </svg>
        </div>

        {/* Gradient Orbs */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-red-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-red-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

        <div className="relative max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="text-sm text-gray-600 mb-8 font-mono-body">
            <Link to="/" className="text-red-700 hover:underline transition-colors">
              Home
            </Link>
            <span className="mx-2">{">"}</span>
            <span className="text-gray-700">Contact Us</span>
          </div>

          {/* Badge */}
          <div className="inline-block mb-6">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-red-50 text-red-700 text-sm font-heading border border-red-200">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              Get in Touch
            </span>
          </div>

          {/* Heading */}
          {/* <h1 className="text-5xl sm:text-6xl md:text-7xl font-display text-gray-900 mb-6 leading-tight">
            Let's Connect
          </h1> */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-display text-gray-900 mb-6 leading-tight">
                Let's {" "}
                <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                  Connect
                </span>
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-gray-600 mb-12 font-mono-body max-w-2xl leading-relaxed">
            If you're ready to transform your business, get in touch with us! We're here to help you achieve your goals.
          </p>

          {/* Form Container */}
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 backdrop-blur-sm bg-opacity-90 border border-gray-100">
            <form className="space-y-8" onSubmit={handleSubmit}>
              {/* Full Name */}
              <div className="relative">
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name*"
                  className="input-field w-full border-b-2 border-gray-300 p-3 focus:outline-none focus:border-red-700 text-gray-900 bg-transparent"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Email */}
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  placeholder="Email*"
                  className="input-field w-full border-b-2 border-gray-300 p-3 focus:outline-none focus:border-red-700 text-gray-900 bg-transparent"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Company Name */}
              <div className="relative">
                <input
                  type="text"
                  name="companyName"
                  placeholder="Company Name*"
                  className="input-field w-full border-b-2 border-gray-300 p-3 focus:outline-none focus:border-red-700 text-gray-900 bg-transparent"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Mobile Number */}
              <div className="relative">
                <input
                  type="tel"
                  name="mobileNumber"
                  placeholder="Mobile Number"
                  className="input-field w-full border-b-2 border-gray-300 p-3 focus:outline-none focus:border-red-700 text-gray-900 bg-transparent"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                />
              </div>

              {/* Requirements */}
              <div className="relative">
                <textarea
                  name="requirements"
                  placeholder="Share your requirements*"
                  rows="5"
                  className="input-field w-full border-b-2 border-gray-300 p-3 focus:outline-none focus:border-red-700 text-gray-900 bg-transparent resize-none"
                  value={formData.requirements}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                className="submit-btn bg-[#8b0000] hover:bg-[#6b0000] text-white px-8 py-4 rounded-lg font-heading text-lg shadow-lg"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Contact Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-heading text-gray-900 mb-2">Email Us</h3>
              <p className="text-gray-600 font-mono-body text-sm">info@astergo.in</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="font-heading text-gray-900 mb-2">Call Us</h3>
              <p className="text-gray-600 font-mono-body text-sm">+ (91) 8796-014195</p>
              <p className="text-gray-600 font-mono-body text-sm">+ (91) 8796-063801</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="font-heading text-gray-900 mb-2">Visit Us</h3>
              <p className="text-gray-600 font-mono-body text-sm">IITL Nimbus Express Parkview 2 , Yamuna Expy Sector Chi V , <br></br>Greater Noida </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactUs;